import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Create the context
const TakeOffContext = createContext();

// Custom hook for easy context usage
export const useTakeOffs = () => useContext(TakeOffContext);

// Provider component
export const TakeOffProvider = ({ children }) => {
  const [takeOffs, setTakeOffs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all takeoffs
  const getAllTakeOffs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        'https://api.ttfconstruction.com/takeOff/readAll.php'
      );
      if (response.data.success) {
        console.log('✅ Takeoffs retrieved successfully:', response.data.takeoffs);
        setTakeOffs(response.data.takeoffs);
      } else {
        console.error('❌ Failed to fetch takeoffs:', response.data.message);
      }
    } catch (error) {
      console.error('❌ Error fetching takeoffs:', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Submit a new takeoff
  const submitNewTakeoff = async (takeoffData) => {
    try {
      const response = await axios.post(
        'https://api.ttfconstruction.com/takeOff/create.php',
        takeoffData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        console.log('✅', response.data.message);
        // Refetch updated list
        await getAllTakeOffs();
        return response.data;
      } else {
        console.error('❌', response.data.message);
        return response.data;
      }
    } catch (error) {
      console.error('❌', error.message);
      return { success: false, message: error.message };
    }
  };

  // Update an existing takeoff
  const updateTakeoff = async (updateData) => {
    try {
      console.log('Sending update request with data:', updateData);
      
      const response = await axios.post(
        'https://api.ttfconstruction.com/takeOff/update.php',
        updateData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Update response:', response.data);

      if (response.data.success) {
        console.log('✅', response.data.message);
        // Refetch the updated list
        await getAllTakeOffs();
        return response.data;
      } else {
        console.error('❌', response.data.message);
        return response.data;
      }
    } catch (error) {
      console.error('❌ Error updating takeoff:', error.message);
      return { success: false, message: error.message };
    }
  };

  // Add to orders
  const addToOrders = async (orderData) => {
    
    try {
      console.log('Sending order data:', orderData);
      
      // Log the full request data for debugging
      console.log('Request payload:', JSON.stringify(orderData));
      
      // Add request timeout and more detailed error handling
      const response = await axios.post(
        'https://api.ttfconstruction.com/takeOff/addToOrders.php',
        orderData, // Send the data directly without wrapping it in another object
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 second timeout
        }
      );

      console.log('Order response:', response.data);

      if (response.data && response.data.success) {
        console.log('✅ Order added successfully');
        return { success: true, message: 'Successfully added to orders!' };
      } else {
        console.error('❌ Failed to add order', response.data);
        return { success: false, message: response.data?.message || 'Failed to add to orders. Please try again.' };
      }
    } catch (error) {
      // More detailed error logging
      console.error('❌ Error adding to orders:', error.message);
      console.error('Error details:', error.response?.data || 'No response data');
      console.error('Error status:', error.response?.status || 'No status code');
      console.error('Error headers:', error.response?.headers || 'No headers');
      
      return { 
        success: false, 
        message: `Error adding to orders: ${error.message}. ${error.response?.data?.message || ''}` 
      };
    }
  };

  // Fetch takeoffs on mount
  useEffect(() => {
    getAllTakeOffs();
  }, []);

  return (
    <TakeOffContext.Provider value={{ 
      takeOffs, 
      loading, 
      getAllTakeOffs, 
      submitNewTakeoff, 
      updateTakeoff,
      addToOrders 
    }}>
      {children}
    </TakeOffContext.Provider>
  );
};
